/**
 * Album API — CRUD альбомов (плейлистов) + управление составом треков.
 *
 * Серверные эндпоинты в backend/app/api/v1/music/albums.py. Бэк отдаёт
 * обложку уже пост-обработанной (manual vs auto) — фронту просто показывать.
 */
import api from './client'
import type { SunoTrack } from './music'

export interface SunoAlbum {
	id: string
	name: string
	/** Эффективная обложка: либо cover_url (manual), либо image_url первого
	 *  трека (auto). Backend уже применил логику. null = нет обложки. */
	cover_url: string | null
	cover_is_manual: boolean
	track_count: number
	created_at: string | null
	updated_at: string | null
}

export interface AlbumsListResponse {
	ok: boolean
	albums: SunoAlbum[]
	total: number
}

export interface AlbumResponse {
	ok: boolean
	album: SunoAlbum
	error?: string
}

export interface AlbumWithTracksResponse {
	ok: boolean
	album: SunoAlbum
	tracks: (SunoTrack & { added_at?: string | null })[]
	error?: string
}

/** GET /api/v1/music/albums — list */
export async function getUserAlbums(): Promise<AlbumsListResponse> {
	// Graceful fallback пока бэк не задеплоил эндпоинт (пустой список).
	// Альбомы — опциональная фича, UI не должен ломаться при их отсутствии.
	try {
		return await api.request<AlbumsListResponse>('/api/v1/music/albums', { method: 'GET' })
	} catch (err) {
		const msg = err instanceof Error ? err.message.toLowerCase() : ''
		if (msg.includes('404') || msg.includes('not found') || msg.includes('request failed')) {
			return { ok: true, albums: [], total: 0 }
		}
		throw err
	}
}

/** POST /api/v1/music/albums */
export async function createAlbum(params: { name: string; cover_url?: string | null }): Promise<AlbumResponse> {
	return api.request<AlbumResponse>('/api/v1/music/albums', {
		method: 'POST',
		body: { name: params.name, cover_url: params.cover_url ?? null },
	})
}

/** GET /api/v1/music/albums/:id — album + its tracks */
export async function getAlbum(albumId: string): Promise<AlbumWithTracksResponse> {
	return api.request<AlbumWithTracksResponse>(`/api/v1/music/albums/${albumId}`, { method: 'GET' })
}

/** PATCH /api/v1/music/albums/:id — rename / change cover */
export async function updateAlbum(
	albumId: string,
	params: { name?: string; cover_url?: string | null; cover_is_manual?: boolean },
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/albums/${albumId}`, { method: 'PATCH', body: params })
}

/** DELETE /api/v1/music/albums/:id — delete album (tracks remain) */
export async function deleteAlbum(albumId: string): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/albums/${albumId}`, { method: 'DELETE' })
}

/** POST /api/v1/music/albums/:id/tracks — добавить треки пачкой (идемпотентно) */
export async function addTracksToAlbum(
	albumId: string,
	trackIds: string[],
): Promise<{ ok: boolean; inserted?: number; error?: string }> {
	return api.request(`/api/v1/music/albums/${albumId}/tracks`, {
		method: 'POST',
		body: { track_ids: trackIds },
	})
}

/** DELETE /api/v1/music/albums/:id/tracks/:track_id — убрать один */
export async function removeTrackFromAlbum(
	albumId: string,
	trackId: string,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/albums/${albumId}/tracks/${trackId}`, {
		method: 'DELETE',
	})
}
